import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Activity, Users, Tag, FileText, Wallet, Shield, RotateCcw, X, Crown,
} from "lucide-react";
import { STORE, subscribe, resetStore } from "@/lib/store";
import { format } from "@/lib/brand-tokens";
import { signInAsAdmin, adminUsers, signOutMember, setDemoPersona } from "@/lib/mock-data";

// Cmd+K / Ctrl+K — global demo state inspector.
// Renders nothing in the layout; mounts a hidden listener and a dialog on demand.
// Shows: current admin + member personas, store counts, recent audit events,
// quick role switcher + persona switcher + reset demo state.
export function DemoStateInspector() {
  const [open, setOpen] = useState(false);
  const [_, force] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      const isOpen = (e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey);
      if (isOpen) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    const onExternalOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-demo-inspector", onExternalOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-demo-inspector", onExternalOpen);
    };
  }, [open]);

  // Re-render on every store mutation so counts stay live while the panel is open.
  useEffect(() => subscribe(() => force((n) => n + 1)), []);

  if (!open) return null;

  const adminId = STORE.manifest?.adminUsers?.SUPER_ADMIN || "adm_001";
  const memberId = STORE.manifest?.members?.DEMO_MEMBER || "mbr_001";
  const admin = STORE.adminUsers.find((a) => a.id === adminId);
  const member = STORE.members.find((m) => m.id === memberId);
  const recentAudit = [...STORE.auditEvents].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 8);

  const stats = [
    { icon: Tag,      label: "Deals",            value: STORE.deals.length,        sub: `${STORE.deals.filter(d => d.status === "Published").length} published` },
    { icon: Users,    label: "Members",          value: STORE.members.length,      sub: `${STORE.members.filter(m => m.status === "Active").length} active` },
    { icon: FileText, label: "Applications",     value: STORE.applications.length, sub: `${STORE.applications.filter(a => a.status === "PendingReview").length} pending` },
    { icon: FileText, label: "Submissions",      value: STORE.submissions.length,  sub: `${STORE.submissions.filter(s => /Flag|Pending|Awaiting/.test(s.status)).length} need review` },
    { icon: Wallet,   label: "Payment runs",     value: STORE.paymentRuns.length,  sub: `${STORE.paymentRuns.filter(r => r.status === "Generated").length} unsent` },
    { icon: Activity, label: "Audit events",     value: STORE.auditEvents.length,  sub: `${STORE.auditEvents.filter(e => e.action === "PermissionDenied").length} denials` },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="demo-inspector" className="max-w-3xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b border-line bg-surface-alt">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="size-8 rounded-md bg-ink text-white grid place-items-center">
                <Activity className="size-4" />
              </div>
              <div>
                <DialogTitle className="font-brand text-h3 text-ink">Demo state inspector</DialogTitle>
                <DialogDescription className="text-caption">
                  Press <kbd className="font-mono bg-surface border border-line rounded px-1 text-[10px]">Esc</kbd> to close · <kbd className="font-mono bg-surface border border-line rounded px-1 text-[10px]">⌘K</kbd> to toggle
                </DialogDescription>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="size-8 rounded-md hover:bg-surface grid place-items-center" aria-label="Close">
              <X className="size-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Active personas */}
          <section>
            <div className="eyebrow uppercase text-ink-hint mb-2">Active personas</div>
            <div className="grid grid-cols-2 gap-3">
              <PersonaCard
                icon={<Crown className="size-3.5" />}
                tone="bg-ink text-white"
                label="Admin"
                title={admin ? `${admin.firstName} ${admin.lastName}` : "—"}
                subtitle={admin?.role || "—"}
              >
                <select
                  data-testid="inspector-role-switcher"
                  value={adminId}
                  onChange={(e) => signInAsAdmin(e.target.value)}
                  className="mt-2 w-full h-8 px-2 bg-surface border border-line rounded text-caption"
                >
                  {adminUsers.map((a) => (
                    <option key={a.id} value={a.id}>{a.firstName} {a.lastName} · {a.role}</option>
                  ))}
                </select>
              </PersonaCard>
              <PersonaCard
                icon={<Users className="size-3.5" />}
                tone="bg-brand-700 text-white"
                label="Member"
                title={member ? `${member.firstName} ${member.lastName}` : "—"}
                subtitle={member?.status || "—"}
              >
                <select
                  data-testid="inspector-persona-switcher"
                  value={memberId}
                  onChange={(e) => setDemoPersona(e.target.value)}
                  className="mt-2 w-full h-8 px-2 bg-surface border border-line rounded text-caption"
                >
                  {STORE.members.slice(0, 20).map((m) => (
                    <option key={m.id} value={m.id}>{m.firstName} {m.lastName} · {m.status}</option>
                  ))}
                </select>
              </PersonaCard>
            </div>
          </section>

          {/* Store snapshot */}
          <section>
            <div className="eyebrow uppercase text-ink-hint mb-2">Store snapshot</div>
            <div className="grid grid-cols-3 gap-2">
              {stats.map((s) => (
                <div key={s.label} className="bg-surface border border-line rounded-md p-3" data-testid={`inspector-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center gap-1.5 text-caption text-ink-hint">
                    <s.icon className="size-3" /> {s.label}
                  </div>
                  <div className="mt-1 text-h3 font-bold tabular-nums text-ink">{s.value}</div>
                  <div className="text-[10px] text-ink-subtle truncate">{s.sub}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Recent audit */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="eyebrow uppercase text-ink-hint">Recent audit events</div>
              <span className="text-[10px] text-ink-hint tabular-nums">{recentAudit.length} shown</span>
            </div>
            <ul className="bg-surface border border-line rounded-md divide-y divide-line/60 overflow-hidden" data-testid="inspector-audit-list">
              {recentAudit.length === 0 && (
                <li className="px-3 py-2.5 text-caption text-ink-subtle">No events yet — start clicking around.</li>
              )}
              {recentAudit.map((e) => (
                <li key={e.id} className="px-3 py-2 flex items-center gap-3">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    e.action === "PermissionDenied" ? "bg-red-50 text-red-700"
                    : e.isDecryption ? "bg-orange-50 text-orange-700"
                    : "bg-brand-50 text-brand-700"
                  }`}>{e.action}</span>
                  <span className="text-caption text-ink-muted flex-1 truncate">{e.description}</span>
                  <span className="text-[10px] text-ink-hint font-mono tabular-nums shrink-0">{format.relative(e.at)}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Actions */}
          <section className="flex flex-wrap gap-2 pt-1 border-t border-line/60 pt-4">
            <button
              data-testid="inspector-reset-btn"
              onClick={() => { resetStore(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-50 hover:bg-amber-100 border border-amber-200 text-caption text-amber-800 font-semibold"
            >
              <RotateCcw className="size-3.5" /> Reset demo state
            </button>
            <button
              data-testid="inspector-signout-member-btn"
              onClick={() => { signOutMember(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-surface hover:bg-surface-alt border border-line text-caption text-ink-muted font-semibold"
            >
              <Shield className="size-3.5" /> Restore default member
            </button>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PersonaCard({ icon, tone, label, title, subtitle, children }) {
  return (
    <div className="bg-surface border border-line rounded-md p-3">
      <div className="flex items-center gap-2">
        <div className={`size-6 rounded grid place-items-center shrink-0 ${tone}`}>{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-ink-hint">{label}</div>
          <div className="text-body-sm font-semibold text-ink truncate">{title}</div>
          <div className="text-caption text-ink-subtle truncate">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}
